import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type VetInfo = {
  atende_domiciliar?: boolean;
  atende_consultorio?: boolean;
  teleconsulta?: boolean;
  especialidades?: string[];
};
export type VetListing = {
  servico_id: number;
  anfitriao?: { nome?: string | null; cidade?: string | null } | null;
  veterinario?: VetInfo;
};
type PetItem = { id: number; nome: string; tipo_pet?: string };
const modalidades = [
  { id: "domicilio", field: "atende_domiciliar", text: "Em casa" },
  { id: "consultorio", field: "atende_consultorio", text: "Consultório" },
  { id: "teleconsulta", field: "teleconsulta", text: "Online" },
] as const;

export default function VetQuickRequestDialog({ listing, cidade, data, onClose }: {
  listing: VetListing | null;
  cidade: string;
  data: string;
  onClose: () => void;
}) {
  const [pets, setPets] = useState<PetItem[]>([]);
  const [petId, setPetId] = useState("");
  const [city, setCity] = useState("");
  const [modality, setModality] = useState("domicilio");
  const [priority, setPriority] = useState<"hoje" | "agendar">("hoje");
  const [preferredDate, setPreferredDate] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sentId, setSentId] = useState<number | null>(null);
  const authenticated = Boolean(window.__AUTH_ID__);

  useEffect(() => {
    if (!listing) return;
    setCity(cidade || listing.anfitriao?.cidade || "");
    const available = modalidades.find(({ field }) => listing.veterinario?.[field]);
    setModality(available?.id || "domicilio");
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    setPriority(data && data > today ? "agendar" : "hoje");
    setPreferredDate(data && data > today ? data : "");
    setError("");
    setSentId(null);
    setDescription("");
    setPetId("");
  }, [listing, cidade, data]);

  useEffect(() => {
    if (!listing || !authenticated) return;
    const controller = new AbortController();
    fetch("/vet/meus-pets", { credentials: "same-origin", headers: { Accept: "application/json" }, signal: controller.signal })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error("Não foi possível carregar seus pets.")))
      .then((data) => setPets(Array.isArray(data.items) ? data.items : []))
      .catch((err) => { if (err.name !== "AbortError") setPets([]); });
    return () => controller.abort();
  }, [listing, authenticated]);

  const enviar = async () => {
    if (!listing || loading) return;
    setError("");
    if (description.trim().length < 10 || !city.trim()) {
      setError("Informe a cidade e descreva a necessidade em pelo menos 10 caracteres.");
      return;
    }
    if (priority === "agendar" && !preferredDate) {
      setError("Selecione uma data para o atendimento.");
      return;
    }
    setLoading(true);
    try {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
      const response = await fetch("/vet/solicitacao-rapida", {
        method: "POST", credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-TOKEN": csrf },
        body: JSON.stringify({ servico_id: listing.servico_id, pet_id: petId ? Number(petId) : null, cidade: city.trim(), modalidade: modality, prioridade: priority, data_preferida: priority === "agendar" ? preferredDate : null, descricao: description.trim() }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || Object.values(payload.errors || {}).flat().join(" ") || `Erro ${response.status}`);
      setSentId(Number(payload.id));
    } catch (err: any) {
      setError(err?.message || "Não foi possível registrar a solicitação.");
    } finally { setLoading(false); }
  };

  const today = (() => { const now = new Date(); return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`; })();
  return (
    <Dialog open={Boolean(listing)} onOpenChange={(open) => { if (!open && !loading) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Solicitar atendimento veterinário</DialogTitle>
          <DialogDescription>{listing?.anfitriao?.nome || "Profissional"} — a consulta só será confirmada após você aceitar a proposta de horário e valor.</DialogDescription>
        </DialogHeader>
        {!authenticated ? (
          <div className="space-y-3 text-sm"><p>Entre na sua conta de tutor para enviar o pedido.</p><a className="font-semibold text-forest underline" href="/login">Entrar na AllePets</a></div>
        ) : sentId ? (
          <div className="space-y-3"><p className="font-semibold">Pedido #{sentId} registrado.</p><p className="text-sm text-muted-foreground">Aguardando proposta do veterinário. Isto ainda não é uma reserva.</p><Link className="text-sm font-semibold text-forest underline" to="/vet/minhas-solicitacoes" onClick={onClose}>Acompanhar meus pedidos</Link></div>
        ) : (
          <div className="space-y-3 text-sm">
            <label className="block font-semibold">Pet
              <select value={petId} onChange={(e) => setPetId(e.target.value)} className="mt-1 block w-full rounded-lg border bg-white p-2 font-normal">
                <option value="">Informar depois</option>{pets.map((pet) => <option key={pet.id} value={pet.id}>{pet.nome} {pet.tipo_pet ? `(${pet.tipo_pet})` : ""}</option>)}
              </select>
            </label>
            <label className="block font-semibold">Cidade<input required maxLength={160} value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 block w-full rounded-lg border p-2 font-normal" /></label>
            <label className="block font-semibold">Atendimento
              <select value={modality} onChange={(e) => setModality(e.target.value)} className="mt-1 block w-full rounded-lg border bg-white p-2 font-normal">
                {modalidades.filter(({ field }) => listing?.veterinario?.[field]).map(({ id, text }) => <option key={id} value={id}>{text}</option>)}
              </select>
            </label>
            <label className="block font-semibold">Quando?
              <select value={priority} onChange={(e) => setPriority(e.target.value as "hoje" | "agendar")} className="mt-1 block w-full rounded-lg border bg-white p-2 font-normal">
                <option value="hoje">Hoje — sujeito a confirmação</option><option value="agendar">Agendar outro dia</option>
              </select>
            </label>
            {priority === "agendar" && <label className="block font-semibold">Data preferida<input type="date" min={today} value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} className="mt-1 block w-full rounded-lg border p-2 font-normal" /></label>}
            <label className="block font-semibold">O que seu pet precisa?
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={3000} rows={3} placeholder="Descreva resumidamente a necessidade; não informe dados pessoais ou documentos." className="mt-1 block w-full rounded-lg border p-2 font-normal" />
            </label>
            <p className="text-xs text-muted-foreground">A descrição será compartilhada com o profissional escolhido. Em emergência grave, procure atendimento veterinário presencial imediato.</p>
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            <button type="button" onClick={enviar} disabled={loading || !modalidades.some(({ field }) => listing?.veterinario?.[field])} className="w-full rounded-xl bg-forest p-3 font-bold text-white disabled:opacity-50">{loading ? "Enviando…" : "Enviar pedido ao veterinário"}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
